<template>
    <!-- アカウント作成セクション -->
    <div class="bg-white m-6 p-12 rounded-lg flex flex-col justify-start items-center gap-8">
        <!-- エラーメッセージ -->
        <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />

        <div class="flex flex-col gap-4">
            <h1 class="text-2xl font-bold text-center">
                チャンネルを作成する
            </h1>
            <p class="text-sm">
                文字起こしされた記事に表示される内容です。アカウント作成後にチャンネル管理画面から変更可能です。
            </p>
            <div class="flex items-center justify-end space-x-1 link-styles">
                <i class="fa-light fa-circle-question"></i>
                <a href="#" @click="showModal = true" class="text-sm">表示サンプルを確認する</a>
            </div>
        </div>
        <div class="flex flex-col gap-6 mt-6 w-full">
            <div class="flex items-center gap-6">
                <div class="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 border border-gray-200 shrink-0">
                    <img v-if="localChannelData.thumbnail_url" :src="localChannelData.thumbnail_url" alt="Channel Thumbnail" class="w-full h-full object-cover">
                    <i v-else class="fa-solid fa-user text-4xl text-gray-500" aria-hidden="true"></i>
                </div>
                <div class="break-all space-y-2">
                    <p class="text-sm font-bold">チャンネルURL</p>
                    <p class="text-sm">{{ localChannelData.channel_url }}</p>
                </div>
            </div>
            <div>
                <div class="flex flex-col gap-2 mb-6">
                    <label class="block text-sm font-bold">
                        アカウント名 <span class="text-alert">必須</span>
                    </label>
                    <textarea v-model="localChannelData.channel_name" placeholder="YouTubeと同じ最大全角50文字のアカウント名" :class="[channelNameClass,'placeholder-placeholder block w-full rounded-md border border-gray-300 p-2']" />
                    <div class="text-right text-xs text-sub">{{ localChannelData.channel_name.length }}/50</div>
                    <p v-if="channelNameError" class="text-alert text-sm font-bold">{{ channelNameError }}</p>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="block text-sm font-bold">
                        チャンネル説明
                    </label>
                    <textarea v-model="localChannelData.description" rows="5" placeholder="チャンネルについて説明してください。説明は、記事内に表示されます。" :class="[descriptionClass,'placeholder-placeholder block w-full rounded-md border border-gray-300 p-2']"></textarea>
                    <div class="text-right text-xs text-sub">{{ localChannelData.description.length }}/160</div>
                    <p v-if="descriptionError" class="text-alert text-sm font-bold">{{ descriptionError }}</p>
                </div>
            </div>
        </div>

        <button @click="createChannel" class="w-full mt-8 text-center btn-lg" type="button" :disabled="!isValid || isSaving" :class="[isValid && !isSaving ? 'btn-primary text-white': 'disabled:btn-disabled disabled:btn-lg']">チャンネルを登録する
        </button>

        <router-link to="/channel-invitation/" class="text-[16px] link-styles">
            キャンセル
        </router-link>
    </div>
<!--サンプルモーダル-->
<div v-if="showModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center p-4">
    <div class="bg-white rounded-lg overflow-hidden shadow max-w-md px-6 py-10 md:p-12 text-center">
        <div class="flex justify-between pb-4 items-center">
            <h3 class="text-xl font-bold">
                表示サンプル
            </h3>
            <button @click="showModal = false" class="link-styles--gray">
                <span class="sr-only">Close</span>
                <i class="fa-light fa-xmark text-2xl" aria-hidden="true"></i>
            </button>
        </div>
        <img :src="sampleImg" alt="Sample" class="object-cover py-4 border-t border-b border-gray-300">
        <div class="mt-4">
            <button @click="showModal = false" class="btn-secondary">
                閉じる
            </button>
        </div>
    </div>
</div>
</template>

    
    
<script>
import {
    ref,
    computed,
    onMounted,
    inject,
} from 'vue';
import axios from 'axios';

import {
    useStore,
} from 'vuex';
import {
    useRouter
} from 'vue-router';
import sampleImg from '@/assets/sampleImg.png';
import ErrorMessage from './ErrorMessage.vue';

export default {
    components: {
        ErrorMessage,
    },
    setup() {
        const store = useStore();
        const router = useRouter();
        const fetchChannels = inject("fetchChannels");
        const showModal = ref(false);
        // 初期値を持つオブジェクトでlocalChannelDataを初期化
        const localChannelData = ref({
            channel_url: '',
            thumbnail_url: '',
            channel_name: '',
            description: ''
        });
        const errorMessage = ref('');
        const isSaving = ref(false); //保存押下状態の管理
        const isAgreed = ref(false); // 利用規約同意チェックボックスの状態

        // Vuexのstoreからデータを取得し、localChannelDataに反映
        onMounted(async () => {
            const channels = await fetchYoutubeChannels();
            if(channels.length > 0) {
                localChannelData.value = channels[0];
            }
        });

        // エラーメッセージとクラス名の算出プロパティ
        const channelNameError = computed(() => {
            if (!localChannelData.value.channel_name) return '必須項目です。';
            if (localChannelData.value.channel_name.length > 50) return '50文字以内で入力してください。';
            return '';
        });

        const descriptionError = computed(() => {
            if (localChannelData.value.description.length > 160) return '160文字以内で入力してください。';
            return '';
        });

        const channelNameClass = computed(() => ({
            'border-red-500': channelNameError.value,
            'border-gray-300': !channelNameError.value,
        }));

        const descriptionClass = computed(() => ({
            'border-red-500': descriptionError.value,
            'border-gray-300': !descriptionError.value,
        }));

        const isValid = computed(() => {
            return (descriptionError.value.length === 0) && (channelNameError.value.length === 0);
        });

        const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
        const accessToken = store.state.accessToken;

        const fetchYoutubeChannels = async () => {
            try {
                const response = await axios.get(`${apiEndpoint}/channels/youtube`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (response.status === 200) {
                    return response.data;
                }
            } catch (error) {
                if (error.response.status === 401) {
                    errorMessage.value = '認証に失敗しました。再度ログインしてからお試しください。';
                    return [];
                } else {
                    errorMessage.value = 'Youtubeチャンネル情報を取得するのに失敗しました。しばらく経ってからお試しください';
                    return [];
                }
            }
        };

        // Channel作成処理
        async function createChannel() {
            // エラーチェック
            if (!isValid.value) {
                errorMessage.value = '入力エラーを修正してください。';
                return;
            }
            if (isSaving.value) {
                return; // 既に保存処理中の場合は何もしない
            }
            isSaving.value = true; // 保存処理を開始するときにボタンを無効化

            const payload = {
                channelData: localChannelData.value,
                action: 'create',
            };

            try {
                const response = await axios.post(`${apiEndpoint}/channels/`, payload, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (response.status === 201) {
                    console.log('Channel is created successfully', response.data);
                    await fetchChannels();
                    router.push({
                        name: 'ChannelCreated'
                    });
                }
            } catch (error) {
                console.error('Error creating account', error);
                errorMessage.value = 'チャンネルの作成に失敗しました。';
            } finally {
                isSaving.value = false; // 処理が完了したらボタンを再び有効化
            }
        }

        return {
            createChannel,
            showModal,
            localChannelData,
            fetchYoutubeChannels,
            errorMessage,
            channelNameError,
            descriptionError,
            channelNameClass,
            descriptionClass,
            sampleImg,
            isValid,
            isSaving,
            isAgreed
        };
    }
};
</script>
